// ── Timesheet daily-hours policy engine ─────────────────────────────────────
// Pure, framework-free rules for a single day's Standard/Overtime/Leave hours.
// No React/antd/JSX here on purpose — DayLogModal (TimesheetComponents.jsx) is
// a thin renderer over `computeHoursPolicy(...)`, and every rule below is
// asserted directly in timesheetHoursPolicy.test.js without rendering
// anything. Modelled on the finance suite's financeApprovalEngine.js (a
// config object + pure compute function).
//
// Day model, three kinds (see resolveDayKind):
//   holiday — no log required at all; the modal hides its form entirely.
//   weekOff — Standard and Leave are not applicable; the day is loggable as
//             Overtime only.
//   working — the ordinary case: Standard + Leave share an 8h regular
//             allocation. Overtime only unlocks once Standard reaches a full
//             8 — a day can't mix "worked less than a full day" with
//             "worked overtime", and since Leave is itself forced to 0 the
//             moment Standard hits 8, this same rule is what keeps Leave and
//             Overtime from ever coexisting on the same day.

export const DEFAULT_HOURS_POLICY = {
  regularDayHours: 8, // the Standard+Leave envelope on a working day
  standardMaxHours: 8, // Standard's own ceiling (equal to regularDayHours today)
  overtimeMaxRegular: 16, // OT cap on a regular working day
  overtimeMaxWeekOff: 24, // OT cap on a week off (the whole day is overtime)
  step: 0.5, // input increment
  decimals: 2, // rounding precision for derived values
  epsilon: 0.001, // float-slop guard, e.g. 7.5 + 0.5 landing on 7.999999999999999
};

export const HOURS_MESSAGES = {
  ST_MAX: 'Standard hours cannot exceed 8 hours.',
  LEAVE_MAX: 'Leave hours cannot exceed 8 hours.',
  LEAVE_REMAINING: 'Leave hours cannot exceed the remaining regular hours.',
  DAY_TOTAL: 'Standard + Leave hours cannot exceed 8 hours.',
  OT_MAX_REGULAR: 'Overtime cannot exceed 16 hours on a regular working day.',
  OT_MAX_WEEKOFF: 'Overtime cannot exceed 24 hours on a week-off.',
  OT_REQUIRES_FULL_STANDARD: 'Overtime can only be entered after completing 8 standard working hours.',
  WEEKOFF_NA: 'Standard and Leave hours are not applicable on a week-off.',
  ST_NEGATIVE: 'Standard hours cannot be negative.',
  OT_NEGATIVE: 'Overtime cannot be negative.',
  LEAVE_NEGATIVE: 'Leave hours cannot be negative.',
};

// Coerces antd's InputNumber values (undefined/null/'' when cleared, or a
// stray non-numeric string) down to a plain finite number.
function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function roundHours(value, decimals = DEFAULT_HOURS_POLICY.decimals) {
  const factor = 10 ** decimals;
  return Math.round(toNumber(value) * factor) / factor;
}

export function clampHours(value, min, max, decimals = DEFAULT_HOURS_POLICY.decimals) {
  return roundHours(Math.min(Math.max(toNumber(value), min), max), decimals);
}

// Holiday wins over week-off when a date is flagged as both — matches the
// modal's existing precedence (`isLocked = day?.isHoliday`), so the engine
// and the JSX never disagree on a day carrying both flags.
export function resolveDayKind(day) {
  if (day?.isHoliday) return 'holiday';
  if (day?.isWeekOff) return 'weekOff';
  return 'working';
}

function pushViolation(violations, field, code, policy, blocking = true) {
  violations.push({ field, code, message: HOURS_MESSAGES[code], blocking });
}

export function computeHoursPolicy(day, values, policy = DEFAULT_HOURS_POLICY) {
  const dayKind = resolveDayKind(day);
  const ST = roundHours(values?.ST, policy.decimals);
  const OT = roundHours(values?.OT, policy.decimals);
  const Leave = roundHours(values?.Leave, policy.decimals);
  const violations = [];

  let fields;
  let overtimeCap;

  if (dayKind === 'holiday') {
    // No manual log required — the modal hides the whole form for this case,
    // so the engine must never block a path that has no Save button.
    overtimeCap = 0;
    fields = {
      ST: { min: 0, max: 0, step: policy.step, disabled: true },
      OT: { min: 0, max: 0, step: policy.step, disabled: true },
      Leave: { min: 0, max: 0, step: policy.step, disabled: true },
    };
  } else if (dayKind === 'weekOff') {
    overtimeCap = policy.overtimeMaxWeekOff;
    fields = {
      ST: { min: 0, max: 0, step: policy.step, disabled: true },
      OT: { min: 0, max: overtimeCap, step: policy.step, disabled: false },
      Leave: { min: 0, max: 0, step: policy.step, disabled: true },
    };
    // A blocking error here would deadlock the user: ST/Leave are disabled,
    // so there is nothing they could type to clear it. This only ever fires
    // on legacy data (a day logged as "working" later reclassified as a week
    // off) — reconcileHours() auto-zeroes it on modal open instead.
    if (ST > policy.epsilon || Leave > policy.epsilon) {
      pushViolation(violations, 'ST', 'WEEKOFF_NA', policy, false);
    }
    if (OT < 0) {
      pushViolation(violations, 'OT', 'OT_NEGATIVE', policy);
    } else if (OT > overtimeCap + policy.epsilon) {
      pushViolation(violations, 'OT', 'OT_MAX_WEEKOFF', policy);
    }
  } else {
    overtimeCap = policy.overtimeMaxRegular;
    let leaveMax = clampHours(policy.regularDayHours - ST, 0, policy.regularDayHours, policy.decimals);
    let stErrored = false;

    if (ST < 0) {
      pushViolation(violations, 'ST', 'ST_NEGATIVE', policy);
      stErrored = true;
    } else if (ST > policy.standardMaxHours + policy.epsilon) {
      pushViolation(violations, 'ST', 'ST_MAX', policy);
      stErrored = true; // skip the leave-remaining check below — ST is already
      // the sole owner of this failure, and 8-9=-1 would otherwise also
      // (spuriously) fault Leave for "exceeding" a negative remainder.
    }

    if (!stErrored) {
      if (Leave < 0) {
        pushViolation(violations, 'Leave', 'LEAVE_NEGATIVE', policy);
      } else if (Leave > policy.regularDayHours + policy.epsilon) {
        // Absolute ceiling, checked before the ST-dependent remaining-hours
        // check below — mirrors ST_MAX. A value like 8888 always gets this
        // flat "cannot exceed 8 hours" message, regardless of what Standard
        // happens to be set to.
        pushViolation(violations, 'Leave', 'LEAVE_MAX', policy);
      } else if (Leave > leaveMax + policy.epsilon) {
        // Leave's own input is disabled exactly when leaveMax is 0 (ST===8) —
        // the message has to live on the only field the user can still move.
        if (leaveMax <= policy.epsilon) {
          pushViolation(violations, 'ST', 'DAY_TOTAL', policy);
        } else {
          pushViolation(violations, 'Leave', 'LEAVE_REMAINING', policy);
        }
      }
    }

    // Overtime only unlocks once Standard reaches a full 8 hours — matches
    // ST_MAX's own +/-epsilon tolerance so a value that just barely rounds to
    // 8 doesn't get treated as "incomplete".
    const otAllowed = Math.abs(ST - policy.standardMaxHours) <= policy.epsilon;
    if (OT < 0) {
      pushViolation(violations, 'OT', 'OT_NEGATIVE', policy);
    } else if (OT > policy.epsilon && !otAllowed) {
      pushViolation(violations, 'OT', 'OT_REQUIRES_FULL_STANDARD', policy);
    } else if (OT > overtimeCap + policy.epsilon) {
      pushViolation(violations, 'OT', 'OT_MAX_REGULAR', policy);
    }

    fields = {
      ST: { min: 0, max: policy.standardMaxHours, step: policy.step, disabled: false },
      OT: { min: 0, max: overtimeCap, step: policy.step, disabled: !otAllowed },
      Leave: { min: 0, max: leaveMax, step: policy.step, disabled: leaveMax <= policy.epsilon },
    };
  }

  const regularAllocated = roundHours(ST + Leave, policy.decimals);
  const regularRemaining =
    dayKind === 'working'
      ? Math.max(0, roundHours(policy.regularDayHours - regularAllocated, policy.decimals))
      : 0;
  const leaveRemaining =
    dayKind === 'working' ? Math.max(0, roundHours(fields.Leave.max - Leave, policy.decimals)) : 0;
  const dayTotal = roundHours(ST + OT + Leave, policy.decimals);

  const errorsByField = { ST: null, OT: null, Leave: null };
  for (const violation of violations) {
    if (violation.blocking && !errorsByField[violation.field]) {
      errorsByField[violation.field] = violation.message;
    }
  }

  return {
    dayKind,
    values: { ST, OT, Leave },
    fields,
    regularAllocated,
    regularRemaining,
    leaveRemaining,
    overtimeCap,
    dayTotal,
    isFull: dayKind === 'working' && regularRemaining <= policy.epsilon,
    violations,
    errorsByField,
    valid: !violations.some((v) => v.blocking),
  };
}

// Cross-field derivation fired from the form's onValuesChange. Returns a
// patch of ONLY the keys that actually need to change, or null when nothing
// does (so the caller can skip a pointless setFieldsValue).
//
// Standard and Leave drive each other symmetrically to fill the 8h regular
// allocation, in both directions — whichever field the user is actively
// editing is the "driver" for that edit, and the OTHER one is recomputed as
// (8 - driver): editing Standard down to 6 fills Leave with 2, and editing
// Leave down to 6 fills Standard with 2, exactly the same way. This only
// ever drives the field the user is NOT currently typing into — an edit to
// Leave never rewrites Leave itself, only Standard, and vice versa.
//
// Crucially, an OUT-OF-RANGE edit never drives the other field: typing 8888
// into Leave must not silently redefine Standard as 0 — it's left exactly as
// is, and computeHoursPolicy's validator is what explains why it's invalid.
// The same value is also never silently clamped back down here (that used to
// happen and is exactly why no error ever appeared for an out-of-range Leave
// entry — the value got silently rewritten to a valid one before the
// validator had anything left to complain about). The lone exception is the
// modal-just-opened case (changedKeys is empty, not a live edit) fixing up a
// legacy Leave value left over from before Standard was last saved.
//
// Overtime is zeroed whenever the (possibly just-updated) Standard isn't
// exactly 8 — the field is disabled in that state anyway (see
// computeHoursPolicy) — checked unconditionally so it also repairs on open.
export function reconcileHours(dayKind, values, changedKeys = [], policy = DEFAULT_HOURS_POLICY) {
  const ST = roundHours(values?.ST, policy.decimals);
  const Leave = roundHours(values?.Leave, policy.decimals);
  const OT = roundHours(values?.OT, policy.decimals);

  if (dayKind === 'weekOff') {
    const patch = {};
    if (ST !== 0) patch.ST = 0;
    if (Leave !== 0) patch.Leave = 0;
    return Object.keys(patch).length ? patch : null;
  }

  if (dayKind === 'working') {
    const patch = {};
    const inRange = (v) => v >= 0 && v <= policy.regularDayHours + policy.epsilon;

    if (changedKeys.includes('ST')) {
      const leaveMax = clampHours(policy.regularDayHours - ST, 0, policy.regularDayHours, policy.decimals);
      if (Leave !== leaveMax) patch.Leave = leaveMax;
    } else if (changedKeys.includes('Leave')) {
      if (inRange(Leave)) {
        const stTarget = clampHours(policy.regularDayHours - Leave, 0, policy.regularDayHours, policy.decimals);
        if (ST !== stTarget) patch.ST = stTarget;
      }
    } else {
      // Modal just opened (no live edit) — repair a Leave value left over
      // from before Standard was last saved, same as before.
      const leaveMax = clampHours(policy.regularDayHours - ST, 0, policy.regularDayHours, policy.decimals);
      if (Leave > leaveMax + policy.epsilon) patch.Leave = leaveMax;
    }

    const effectiveST = patch.ST !== undefined ? patch.ST : ST;
    const otAllowed = Math.abs(effectiveST - policy.standardMaxHours) <= policy.epsilon;
    if (!otAllowed && OT > policy.epsilon) {
      patch.OT = 0;
    }
    return Object.keys(patch).length ? patch : null;
  }

  // holiday: nothing to reconcile, the form is hidden.
  return null;
}

export function describeCapacity(snapshot) {
  if (!snapshot) return '';
  if (snapshot.dayKind === 'holiday') return '';
  if (snapshot.dayKind === 'weekOff') {
    return `Week off — overtime only, up to ${snapshot.overtimeCap} hrs.`;
  }
  if (snapshot.isFull) {
    return 'Maximum standard hours reached — 8 regular hours allocated.';
  }
  return `Maximum available leave: ${snapshot.leaveRemaining} hrs`;
}
